import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '../api/http.ts';
import { useAuth } from '../store/auth.ts';

export type SocketState = 'connecting' | 'online' | 'offline';

interface ServerMessage {
  type: 'hello' | 'delta' | 'quota' | 'notice' | 'revoked' | 'pong';
  namespace_id?: string;
  seq?: number;
  used?: number;
  message?: string;
}

const PING_INTERVAL = 30_000;
const MAX_BACKOFF = 60_000;

/**
 * 실시간 알림 구독.
 * WS 는 '무언가 바뀌었다' 는 신호만 주고, 실제 데이터는 쿼리 무효화로 다시 가져온다.
 * 따라서 메시지를 놓쳐도 다음 사용자 조작에서 복구된다.
 */
export function useSocket(): SocketState {
  const queryClient = useQueryClient();
  const status = useAuth((s) => s.status);
  const [state, setState] = useState<SocketState>('connecting');
  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const closedRef = useRef(false);

  useEffect(() => {
    if (status !== 'authenticated') return;
    closedRef.current = false;
    let pingTimer: number | undefined;
    let retryTimer: number | undefined;

    const connect = (): void => {
      const token = getAccessToken();
      if (!token) { retryTimer = window.setTimeout(connect, 1000); return; }

      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const socket = new WebSocket(
        `${scheme}://${window.location.host}/ws?token=${encodeURIComponent(token)}`,
      );
      socketRef.current = socket;
      setState('connecting');

      socket.onopen = () => {
        attemptRef.current = 0;
        setState('online');
        pingTimer = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping', t: Date.now() }));
          }
        }, PING_INTERVAL);
      };

      socket.onmessage = (event: MessageEvent<string>) => {
        let msg: ServerMessage;
        try { msg = JSON.parse(event.data) as ServerMessage; } catch { return; }

        if (msg.type === 'delta' && msg.namespace_id) {
          void queryClient.invalidateQueries({ queryKey: ['nodes', msg.namespace_id] });
          void queryClient.invalidateQueries({ queryKey: ['trash', msg.namespace_id] });
          void queryClient.invalidateQueries({ queryKey: ['usage'] });
        } else if (msg.type === 'quota' && typeof msg.used === 'number') {
          useAuth.getState().setUsage(msg.used);
        } else if (msg.type === 'revoked') {
          closedRef.current = true;
          void useAuth.getState().logout();
        }
      };

      socket.onclose = () => {
        window.clearInterval(pingTimer);
        setState('offline');
        if (closedRef.current) return;
        // 지수 백오프 + 지터 — 서버 재시작 시 동시 재접속 폭주를 피한다
        const base = Math.min(MAX_BACKOFF, 1000 * 2 ** attemptRef.current++);
        const jitter = base * (0.8 + Math.random() * 0.4);
        retryTimer = window.setTimeout(connect, jitter);
      };

      socket.onerror = () => { socket.close(); };
    };

    connect();
    return () => {
      closedRef.current = true;
      window.clearInterval(pingTimer);
      window.clearTimeout(retryTimer);
      socketRef.current?.close();
    };
  }, [status, queryClient]);

  return state;
}
